// Admin user management for the Permissions page.
//
// These talk to the users function, which owns the full create/update/delete set keyed
// by Mongo id. The older helpers in api.ts address a different endpoint by email and are
// left alone so the existing Users page keeps working.

import { getToken } from './auth';
import { SitePermissions } from '../config/sitePermissions';

const API_URL = '/.netlify/functions';

export interface ManagedUser {
  _id: any;
  uname: string;
  role: string;
  active: boolean;
  clientName?: string | null;
  /** Job title shown on the stand-alone Ticket Hub, e.g. Marketing or Sales. */
  jobTitle?: string | null;
  clients?: string[];
  assignedShipments?: string[];
  sitePermissions?: SitePermissions;
  lastLoginAt?: string;
  lastLoginSite?: string;
  /** When an admin last ended this person's sign-ins. */
  sessionsEndedAt?: string | null;
  lastPage?: string;
  lastPageSite?: string;
  lastPageAt?: string;
}

const authHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
};

const parse = async (response: Response, action: string) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.message || `${action} failed`);
  return data;
};

export const idOf = (user: { _id: any }): string =>
  typeof user._id === 'object' && user._id?.$oid ? user._id.$oid : String(user._id);

export const listUsers = async (): Promise<ManagedUser[]> => {
  const data = await parse(await fetch(`${API_URL}/users`, { headers: authHeaders() }), 'Loading users');
  return Array.isArray(data) ? data : data?.users || [];
};

export const createManagedUser = async (user: Partial<ManagedUser> & { password: string }) =>
  parse(
    await fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(user),
    }),
    'Creating the user',
  );

export const updateManagedUser = async (id: string, updates: Partial<ManagedUser> & { password?: string }) =>
  parse(
    await fetch(`${API_URL}/users/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(updates),
    }),
    'Saving the user',
  );

/** End every sign-in this person has, on every device. They keep their password. */
export const signOutManagedUser = async (id: string) =>
  parse(
    await fetch(`${API_URL}/users/${id}/sign-out`, { method: 'POST', headers: authHeaders() }),
    'Signing the user out',
  );

export const deleteManagedUser = async (id: string) =>
  parse(
    await fetch(`${API_URL}/users/${id}`, { method: 'DELETE', headers: authHeaders() }),
    'Deleting the user',
  );

/** Report the page the signed-in user is on. Best effort: never surfaced to the user. */
export const recordPageVisit = (page: string, site = 'portal') => {
  const token = getToken();
  if (!token) return;
  fetch(`${API_URL}/user-activity`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ site, page }),
  }).catch(() => { /* activity tracking must never disturb the page */ });
};
